import * as React from 'react'
import { Navigate, useNavigate } from 'react-router-dom'

import { Form } from 'factorial-form'
import { NewSurveyPageDocument } from 'generated/resources/graphql'

import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import asyncAction, { isSuccessful } from 'modules/core/lib/asyncAction'

import AccessGroupForm from 'modules/surveys/components/AccessGroupForm'
import NewSurveyModal from 'modules/surveys/components/New/NewSurveyModal'
import { Types } from 'modules/surveys/lib/enums'
import surveys from 'modules/surveys/store/collections/v2/surveys'

export default function NewSurveyPage() {
  const navigate = useNavigate()
  const { navigation } = useNavigation()

  const { data, loading: loadingPolicies } = useQuery(NewSurveyPageDocument)

  const canAccessSurveysCreateForms = data?.apiCore._policies.accessSurveysCreateForms.can ?? false

  const onClose = () => {
    track(trackingEvents.surveys.createSurveyCancel, { exit_step: 2 })
    navigate(navigation.engagement.surveys.fullPath())
  }

  const [loading, setLoading] = React.useState(false)

  const accessGroupForm = React.useRef(
    new AccessGroupForm({
      strategy: null,
      groupable_id: null,
      groupable_type: null,
    })
  ).current

  const form = React.useRef(
    new Form(
      {
        name: null,
      },
      {
        name: 'string',
      }
    )
  ).current

  const onSubmit = async (closePortal: ClosePortalFn) => {
    setLoading(true)

    const result = await asyncAction(() =>
      surveys.create({
        name: form.get('name').value,
        question_group_config: accessGroupForm.data(),
      })
    )

    setLoading(false)

    if (isSuccessful(result)) {
      const { value } = result
      closePortal({
        preventOnCloseCallbackAfterClose: true,
      })

      track(trackingEvents.surveys.createSurveySubmit, { type: Types.custom })
      navigate(navigation.engagement.surveys.id.configuration.fullPath({ id: value.id }))
    }
  }

  if (!loadingPolicies && !canAccessSurveysCreateForms) {
    return <Navigate to={navigation.engagement.surveys.fullPath()}></Navigate>
  }

  return (
    <NewSurveyModal
      loading={loading}
      form={form}
      accessGroupForm={accessGroupForm}
      onSubmit={onSubmit}
      onClose={onClose}
    />
  )
}
